import { useState, type ReactNode } from 'react';
import { Archive, Camera, Gauge, PlugZap, RotateCw, ScanSearch, SearchCheck, X, type LucideIcon } from 'lucide-react';
import { BULK_LIMITS, BULK_OPERATIONS, type BulkOperation, type Permission } from '@cp/shared';
import { Button, ConfirmDialog, Field, Input } from '@/components/ui';
import { useRoles } from '@/hooks/useAuth';
import { useCreateBulkRun } from '@/hooks/useBulk';
import { toast } from '@/store/toastStore';
import { errorCode, errorText } from '@/utils/errors';
import { plural } from '@/utils/format';
import { can } from '@/utils/permissions';

const ACTIONS: readonly { operation: BulkOperation; icon: LucideIcon; permission: Permission }[] = [
  { operation: 'seo_check', icon: SearchCheck, permission: 'sites.deploy' },
  { operation: 'preview', icon: Camera, permission: 'sites.deploy' },
  { operation: 'platform_detect', icon: ScanSearch, permission: 'sites.deploy' },
  { operation: 'search_console', icon: PlugZap, permission: 'domains.manage' },
  { operation: 'seo_metrics', icon: Gauge, permission: 'sites.deploy' },
  { operation: 'backup', icon: Archive, permission: 'backups.create' },
  { operation: 'restart', icon: RotateCw, permission: 'sites.deploy' },
];

/**
 * What can be done to the sites chosen in the list at once (ADR-0046). A
 * risky action asks for the number of sites to be typed, and takes ten at most.
 */
export function BulkBar({ siteIds, onClear, onLaunched }: { readonly siteIds: readonly string[]; readonly onClear: () => void; readonly onLaunched: (runId: string) => void }): ReactNode {
  const roles = useRoles();
  const [confirming, setConfirming] = useState<BulkOperation | null>(null);
  const [typed, setTyped] = useState('');
  const create = useCreateBulkRun({
    onSuccess: (run) => {
      setConfirming(null);
      setTyped('');
      const skipped = run.counts.skipped;
      toast.success(
        BULK_OPERATIONS[run.operation].instant ? 'Запрошено для выбранных сайтов' : 'Массовое действие запущено',
        skipped > 0 ? `Пропущено сайтов: ${skipped}` : undefined,
      );
      onLaunched(run.id);
    },
    onError: (error) => {
      if (!confirming) toast.error('Не удалось запустить', errorText(error));
    },
  });

  const launch = (operation: BulkOperation) => {
    if (BULK_OPERATIONS[operation].risky) {
      setConfirming(operation);
      return;
    }
    create.mutate({ operation, siteIds: [...siteIds] });
  };

  return (
    <div className="bulk-bar" role="toolbar" aria-label="Действия с выбранными сайтами">
      <span className="strong">Выбрано: {siteIds.length}</span>
      {ACTIONS.filter((action) => can(roles, action.permission)).map(({ operation, icon }) => (
        <Button
          key={operation}
          size="sm"
          icon={icon}
          busy={create.isPending && create.variables?.operation === operation}
          disabled={BULK_OPERATIONS[operation].risky && siteIds.length > BULK_LIMITS.risky}
          title={BULK_OPERATIONS[operation].risky && siteIds.length > BULK_LIMITS.risky ? `Не больше ${BULK_LIMITS.risky} сайтов за раз` : undefined}
          onClick={() => launch(operation)}
        >
          {BULK_OPERATIONS[operation].label}
        </Button>
      ))}
      <Button size="sm" variant="ghost" icon={X} onClick={onClear}>
        Сбросить выбор
      </Button>

      <ConfirmDialog
        open={confirming !== null}
        title={confirming ? `${BULK_OPERATIONS[confirming].label}: ${plural(siteIds.length, 'сайт', 'сайта', 'сайтов')}?` : ''}
        description="Сначала выполняется на одном сайте, затем на остальных. После двух сбоев выполнение останавливается."
        confirmLabel="Запустить"
        busy={create.isPending}
        error={
          create.isError && confirming
            ? errorCode(create.error) === 'CONFIRMATION_MISMATCH'
              ? `Введено не то число: выбрано ${plural(siteIds.length, 'сайт', 'сайта', 'сайтов')}`
              : errorText(create.error)
            : undefined
        }
        onConfirm={() => confirming && create.mutate({ operation: confirming, siteIds: [...siteIds], confirmation: typed.trim() })}
        onClose={() => {
          setConfirming(null);
          setTyped('');
          create.reset();
        }}
      >
        <Field label={`Для подтверждения введите число сайтов: ${siteIds.length}`} htmlFor="bulk-confirm">
          <Input id="bulk-confirm" value={typed} onChange={(event) => setTyped(event.target.value)} inputMode="numeric" autoComplete="off" />
        </Field>
      </ConfirmDialog>
    </div>
  );
}
